import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  LocationData,
  ShareLocationData,
  getSharedLocationLive,
} from 'common/src/actions/locationShare';
import {
  Box,
  Alert,
  alpha,
  Container,
  Typography,
  CircularProgress,
} from '@mui/material';

const mapContainerStyle = {
  width: '100dvw',
  height: '100dvh',
};

const mapOptions: google.maps.MapOptions = {
  zoom: 17,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  gestureHandling: 'greedy',
  disableDefaultUI: false,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }
  ]
};

interface ShareLocationLiveData {
  shareInfo: ShareLocationData;
  location: LocationData | null;
}

function LocationView() {
  const { t } = useTranslation();
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareData, setShareData] = useState<ShareLocationLiveData | null>(null);

  const fetchLocationData = useCallback(async () => {
    if (!shareId) return;

    try {
      const result = await getSharedLocationLive(shareId);

      if (result.success && result.data) {
        setShareData(result.data);
        setError(null);

        if (result.data.location && mapRef.current) {
          const { lat, lng } = result.data.location;
          mapRef.current.panTo({ lat, lng });

          const marker = document.querySelector('.location-marker');
          if (marker) {
            marker.classList.remove('pulse');
            void (marker as HTMLElement).offsetWidth;
            marker.classList.add('pulse');
          }
        }
      } else {
        setError(result.error || t("UNABLE_TO_LOAD_LOCATION"));
      }
    } catch (err: any) {
      setError(err.message || t("ERROR_WHEN_SEARCHING_FOR_LOCATION"));
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    fetchLocationData();

    const intervalId = setInterval(fetchLocationData, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchLocationData]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const getMarkerIcon = (userType: string) => {
    return userType === 'driver'
      ? 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
      : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
      .location-marker.pulse {
        animation: pulse 0.5s ease-in-out;
      }
      .update-badge {
        animation: slideIn 0.3s ease-out;
      }
      @keyframes slideIn {
        from { transform: translateY(-10px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading && !shareData) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100dvh',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          height: '100dvh',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Alert
          severity="error"
          variant="outlined"
          sx={{
            maxWidth: 400,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("THE_SHARING_LINK_MAY_HAVE_EXPIRED_OR_BEEN_CANCELED")}
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100dvh' }}>
      <Box sx={{ position: 'relative', height: '100%' }}>
        <GoogleMap
          onLoad={onLoad}
          options={mapOptions}
          onUnmount={onUnmount}
          mapContainerStyle={mapContainerStyle}
          center={{
            lat: shareData?.location?.lat || 0,
            lng: shareData?.location?.lng || 0
          }}
        >
          {shareData?.location && (
            <Marker
              position={{
                lat: shareData.location.lat,
                lng: shareData.location.lng
              }}
              icon={{
                url: getMarkerIcon(shareData.shareInfo.usertype),
                scaledSize: new window.google.maps.Size(50, 50)
              }}
              animation={window.google.maps.Animation.DROP}
            />
          )}
        </GoogleMap>
      </Box>

      <Box
        sx={{
          py: 1,
          px: 3,
          left: '50%',
          bottom: 20,
          zIndex: 1000,
          borderRadius: 10,
          textAlign: 'center',
          position: 'absolute',
          whiteSpace: 'nowrap',
          backdropFilter: 'blur(4px)',
          transform: 'translateX(-50%)',
          bgcolor: alpha('#000', 0.5),
        }}
      >
        <Typography variant="caption" sx={{ color: 'white', opacity: 0.7 }}>
          {t("REAL_TIME_LOCATION")}
        </Typography>
      </Box>
    </Box>
  );
}

export default LocationView;